<script setup>
import { computed, useId } from 'vue'
import { useFormFieldContext } from '@/composables/useFormFieldContext'

const props = defineProps({
  modelValue: { type: [String, Number, Boolean], default: '' },
  /** List of { value, label, disabled? }. */
  options: { type: Array, required: true },
  name: { type: String, default: '' },
  id: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  invalid: { type: Boolean, default: null },
  density: { type: String, default: '' },
  /** Layout direction */
  direction: {
    type: String,
    default: 'vertical',
    validator: (v) => ['vertical', 'horizontal'].includes(v),
  },
})

const emit = defineEmits(['update:modelValue'])
const autoName = useId()
const { resolveId, resolveDescribedby, resolveInvalid, resolveDensity } = useFormFieldContext()

const groupName = computed(() => props.name || autoName)
const groupId = computed(() => resolveId(props.id))
const resolvedInvalid = computed(() => resolveInvalid(props.invalid))
const resolvedDensity = computed(() => resolveDensity(props.density))
const describedby = computed(() => resolveDescribedby())
</script>

<template>
  <div
    :id="groupId"
    class="form-radio-group"
    :class="{
      'form-radio-group--horizontal': direction === 'horizontal',
      'form-radio-group--compact': resolvedDensity === 'compact',
    }"
    role="radiogroup"
    :aria-invalid="resolvedInvalid ? true : undefined"
    :aria-describedby="describedby"
  >
    <label
      v-for="(opt, idx) in options"
      :key="String(opt.value)"
      class="form-check"
      :class="{
        'form-check--compact': resolvedDensity === 'compact',
        'form-check--disabled': disabled || opt.disabled,
        'is-invalid': resolvedInvalid,
      }"
    >
      <input
        :id="`${groupId}-${idx}`"
        type="radio"
        class="form-check-input"
        :name="groupName"
        :value="opt.value"
        :checked="modelValue === opt.value"
        :disabled="disabled || opt.disabled"
        @change="$emit('update:modelValue', opt.value)"
      />
      <span class="form-check-label">{{ opt.label }}</span>
    </label>
  </div>
</template>
